/**
 * Shared permission catalog and role defaults (used by React + Express).
 * DB (permissions / tenant_roles) is authoritative for RLS; this file is a UX/API cache.
 */

const PERMISSION_CATALOG = [
  { key: 'dashboard.view', label: 'View dashboard', category: 'Core' },

  { key: 'leads.view', label: 'View leads', category: 'Sales' },
  { key: 'leads.create', label: 'Create leads', category: 'Sales' },
  { key: 'leads.edit', label: 'Edit leads', category: 'Sales' },
  { key: 'leads.delete', label: 'Delete leads', category: 'Sales' },
  { key: 'proposals.view', label: 'View proposals', category: 'Sales' },
  { key: 'proposals.create', label: 'Create proposals', category: 'Sales' },
  { key: 'proposals.edit', label: 'Edit proposals', category: 'Sales' },
  { key: 'proposals.approve', label: 'Approve/send proposals', category: 'Sales' },
  { key: 'contracts.view', label: 'View contracts', category: 'Sales' },
  { key: 'contracts.create', label: 'Create contracts', category: 'Sales' },
  { key: 'contracts.edit', label: 'Edit contracts', category: 'Sales' },

  { key: 'jobs.view', label: 'View jobs', category: 'Operations' },
  { key: 'jobs.create', label: 'Create jobs', category: 'Operations' },
  { key: 'jobs.edit', label: 'Edit jobs', category: 'Operations' },
  { key: 'jobs.dispatch', label: 'Dispatch calendar / assign crews', category: 'Operations' },
  { key: 'jobs.cancel', label: 'Cancel jobs', category: 'Operations' },
  { key: 'time.clock', label: 'Clock in/out (own time)', category: 'Operations' },
  { key: 'time.review', label: 'Review all employee time', category: 'Operations' },
  { key: 'time.review_crew', label: 'Review crew member time', category: 'Operations' },
  { key: 'qc.view', label: 'View QC visits', category: 'Operations' },
  { key: 'qc.schedule', label: 'Manage QC schedule', category: 'Operations' },
  { key: 'qc.inspect', label: 'Submit QC inspection results', category: 'Operations' },
  { key: 'review.view', label: 'View job review queue', category: 'Operations' },
  { key: 'review.approve', label: 'Approve completed jobs', category: 'Operations' },

  { key: 'customers.view', label: 'View customers', category: 'Customers & Properties' },
  { key: 'customers.edit', label: 'Create/edit customers', category: 'Customers & Properties' },
  { key: 'properties.view', label: 'View properties', category: 'Customers & Properties' },
  { key: 'properties.edit', label: 'Create/edit properties', category: 'Customers & Properties' },
  { key: 'properties.city_records', label: 'Refresh city records / view summons', category: 'Customers & Properties' },

  { key: 'financial.billing', label: 'Billing', category: 'Financial' },
  { key: 'financial.invoices', label: 'Invoice generation', category: 'Financial' },
  { key: 'financial.payments', label: 'Payments', category: 'Financial' },
  { key: 'financial.pnl', label: 'P&L dashboard', category: 'Financial' },
  { key: 'financial.quickbooks', label: 'QuickBooks sync', category: 'Financial' },
  { key: 'financial.pricing_view', label: 'See customer pricing', category: 'Financial' },

  { key: 'expenses.submit', label: 'Submit job / general expenses', category: 'Financial' },
  { key: 'expenses.review', label: 'Review and approve expenses', category: 'Financial' },

  { key: 'communication.inbox', label: 'Inbox / messaging', category: 'Communication' },
  { key: 'communication.inbox_admin', label: 'Manage mailboxes / view all inboxes', category: 'Communication' },
  { key: 'communication.chat', label: 'Internal team chat', category: 'Communication' },
  { key: 'communication.bridge', label: 'Patch field rep into customer conversation', category: 'Communication' },
  { key: 'reports.view', label: 'Reports', category: 'Communication' },
  { key: 'client_portal.view', label: 'Client portal', category: 'Communication' },

  { key: 'settings.general', label: 'Company settings', category: 'System' },
  { key: 'settings.service_catalog', label: 'Service catalog', category: 'System' },
  { key: 'settings.combination_rules', label: 'Combination rules', category: 'System' },
  { key: 'settings.visit_templates', label: 'Visit group templates', category: 'System' },
  { key: 'settings.billing_milestone_templates', label: 'Billing milestone templates', category: 'System' },
  { key: 'settings.checklist_templates', label: 'Checklist templates', category: 'System' },
  { key: 'settings.system_logs', label: 'System logs', category: 'System' },
  { key: 'settings.city_sync', label: 'City records sync logs', category: 'System' },
  { key: 'users.view', label: 'View user list', category: 'System' },
  { key: 'users.manage', label: 'Add/edit users, roles, permissions', category: 'System' },
  { key: 'users.invite', label: 'Send email invites', category: 'System' },
  { key: 'team.view', label: 'View employees and crews', category: 'System' },
  { key: 'team.manage', label: 'Add/edit employees and crews', category: 'System' },
]

const ALL_PERMISSION_KEYS = PERMISSION_CATALOG.map((p) => p.key)

const USER_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'office', label: 'Office' },
  { value: 'field_manager', label: 'Field Manager' },
  { value: 'crew_leader', label: 'Crew Leader' },
  { value: 'crew_member', label: 'Crew Member' },
  { value: 'qc_inspector', label: 'QC Inspector' },
  { value: 'customer', label: 'Customer' },
]

const LEGACY_ROLE_MAP = {
  manager: 'office',
  dispatcher: 'field_manager',
  crew: 'crew_member',
}

function normalizeRole(role) {
  if (!role) return 'crew_member'
  const key = String(role).trim().toLowerCase()
  return LEGACY_ROLE_MAP[key] || key
}

function expandPatterns(keys) {
  const out = new Set()
  for (const k of keys) {
    if (k.endsWith('.*')) {
      const prefix = k.slice(0, -1)
      for (const p of ALL_PERMISSION_KEYS) {
        if (p.startsWith(prefix)) out.add(p)
      }
    } else {
      out.add(k)
    }
  }
  return out
}

const ROLE_DEFAULTS = {
  admin: ALL_PERMISSION_KEYS,

  office: [
    'dashboard.view',
    'leads.*',
    'proposals.*',
    'contracts.*',
    'customers.*',
    'properties.*',
    'financial.*',
    'expenses.submit',
    'expenses.review',
    'reports.view',
    'communication.inbox',
    'communication.chat',
    'communication.bridge',
    'settings.general',
    'settings.service_catalog',
    'users.view',
    'users.manage',
    'users.invite',
    'team.view',
    'team.manage',
    'jobs.cancel',
  ],

  field_manager: [
    'dashboard.view',
    'jobs.*',
    'time.clock',
    'time.review',
    'customers.view',
    'properties.view',
    'properties.edit',
    'qc.view',
    'qc.schedule',
    'qc.inspect',
    'review.view',
    'review.approve',
    'expenses.submit',
    'communication.chat',
    'communication.bridge',
  ],

  crew_leader: [
    'dashboard.view',
    'jobs.view',
    'jobs.edit',
    'time.clock',
    'time.review_crew',
    'properties.view',
    'expenses.submit',
    'communication.chat',
  ],

  crew_member: [
    'dashboard.view',
    'jobs.view',
    'time.clock',
    'properties.view',
    'expenses.submit',
    'communication.chat',
  ],

  qc_inspector: [
    'dashboard.view',
    'jobs.view',
    'properties.view',
    'qc.view',
    'qc.schedule',
    'qc.inspect',
    'review.view',
    'communication.chat',
  ],

  customer: ['client_portal.view'],
}

function getRoleDefaultPermissions(role) {
  const normalized = normalizeRole(role)
  const patterns = ROLE_DEFAULTS[normalized] || []
  return Array.from(expandPatterns(patterns))
}

/**
 * @param {string} role
 * @param {Array<{permission_key: string, granted: boolean}>|Record<string, boolean>} overrides
 * @param {boolean} isActive
 */
function resolveEffectivePermissions(role, overrides = [], isActive = true) {
  // Only explicit false is inactive — null/undefined must not wipe all perms.
  if (isActive === false) return new Set()

  const base = new Set(getRoleDefaultPermissions(role))
  const list = Array.isArray(overrides)
    ? overrides
    : Object.entries(overrides || {}).map(([permission_key, granted]) => ({ permission_key, granted }))

  for (const o of list) {
    if (!o || !o.permission_key) continue
    if (o.granted) base.add(o.permission_key)
    else base.delete(o.permission_key)
  }
  return base
}

function hasPermission(role, overrides, perm, isActive = true) {
  return resolveEffectivePermissions(role, overrides, isActive).has(perm)
}

function isAdminRole(role) {
  return normalizeRole(role) === 'admin'
}

function assignableRolesForActor(actorRole) {
  if (isAdminRole(actorRole)) return USER_ROLES
  return USER_ROLES.filter((r) => r.value !== 'admin')
}

function canManageTargetUser(actorRole, targetRole) {
  if (!isAdminRole(actorRole) && isAdminRole(targetRole)) return false
  return true
}

module.exports = {
  PERMISSION_CATALOG,
  ALL_PERMISSION_KEYS,
  USER_ROLES,
  LEGACY_ROLE_MAP,
  ROLE_DEFAULTS,
  normalizeRole,
  getRoleDefaultPermissions,
  resolveEffectivePermissions,
  hasPermission,
  isAdminRole,
  assignableRolesForActor,
  canManageTargetUser,
}
